import { apiApplicants } from "@/api/recruit/applicant.api.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ActionItem } from "@/pages/recruit/components/ActionItem.tsx";
import { useApplicantsSearchInfinite } from "@/queries/recruit/use-applicants.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiApplicantShort, ApplicantFilterDefault } from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Table,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface PreviewCandidatesModalProps extends ModalProps {
  applicantIds: number[];
}

export const PreviewCandidatesModal = ({ applicantIds, ...props }: PreviewCandidatesModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { hasField } = useEntityFormConfig(ApiCFEntityTypes.applicant);

  const { isExporting, downloadFile } = useDownloadHelper();

  //https://www.mantine-react-table.com/docs/examples/infinite-scrolling
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: applicants,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useApplicantsSearchInfinite({
    applicant_ids: applicantIds,
    page_number: 1,
    sorting: ApplicantFilterDefault.sorting,
  });

  const flatData = useMemo(
    () => applicants?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [applicants]
  );

  const handleExportClick = useCallback(() => {
    downloadFile({
      apiCall: () =>
        apiApplicants.export({
          page_number: 1,
          applicant_ids: applicantIds,
          sorting: ApplicantFilterDefault.sorting,
        }),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.dataExported, {
          [analyticProperties.title]: "Candidates",
        });
      },
    });
  }, []);

  const fetchMoreOnBottomReached = useCallback(
    (scrollHeight: number, scrollTop: number, clientHeight: number) => {
      if (
        scrollHeight - scrollTop - clientHeight < 400 &&
        !isFetching &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, isFetching, hasNextPage, isFetchingNextPage]
  );

  const columns = useMemo<ColumnDef<ApiApplicantShort>[]>(
    () => [
      {
        header: t("core:name"),
        cell: (row) => (
          <ListItem
            to={`/recruitment/candidates/${row.row.original.id}/`}
            blank={true}
            avatarUrl={row.row.original.photo_url}
            behavior={"card"}
            preTitle={`${row.row.original.first_name} ${row.row.original.last_name}`}
            title={
              hasField(ApiFieldTypes.applicant_position)
                ? (row.row.original.current_position.position ?? "-")
                : undefined
            }
            caption={
              hasField(ApiFieldTypes.applicant_company)
                ? (row.row.original.current_position.company ?? "-")
                : undefined
            }
            emphasis={"top"}
            forceShowAvatar={true}
          />
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:last_action"),
        cell: (row) =>
          row.row.original.last_action ? (
            <ActionItem action={row.row.original.last_action} dimmed />
          ) : (
            "-"
          ),
        accessorKey: "position",
        ...ColumnSizes.status,
      },
      {
        header: "",
        cell: (row) => (
          <Stack items={"start"} justify={"start"} className={"w-full py-3 grow"}>
            {row.row.original.candidates.slice(0, 2).map((candidate) => (
              <Stack
                direction={"horizontal"}
                gap={"xs"}
                className={"w-full"}
                key={`${candidate.id}`}
              >
                <Text>{candidate.job.title} </Text>
                <Text className={"text-secondary"}>{" · "}</Text>
                {candidate.disqualification_reason ? (
                  <Text className={"text-secondary"}>
                    {candidate.disqualification_reason.title}
                  </Text>
                ) : (
                  <Text className={"text-secondary"}>{candidate.pipeline_stage.title}</Text>
                )}
              </Stack>
            ))}
          </Stack>
        ),
        accessorKey: "candidates",
        ...ColumnSizes.status,
      },
    ],
    [flatData, hasField]
  );
  return (
    <Modal
      {...props}
      layout="stretch"
      size={"xl"}
      title={`${t("plurals:plural_candidates", { count: applicantIds.length })}`}
      withCloser
      closeByEscEnabled
      onModalContentScroll={(scrollTop, scrollHeight, height) => {
        fetchMoreOnBottomReached(scrollHeight, scrollTop, height);
      }}
      headerActionsSlot={
        canIEdit(ApiPermission.manage_candidates) && canIEdit(ApiPermission.reporting_export) ? (
          <Button
            isLoading={isExporting}
            onClick={handleExportClick}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
            variant={"secondary"}
          >
            {t("common:export_to_xlsx")}
          </Button>
        ) : undefined
      }
      actions={
        <Stack gap={"sm"}>
          <Button onClick={() => close(id)} size={"lg"} className={"w-full"}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Box className={"relative"}>
        <Stack className={"overflow-hidden"}>
          <Box ref={tableContainerRef} className={"overflow-y-auto h-full"}>
            <Table data={flatData} columns={columns} showSkeleton={isLoading} />
            {isFetching && <Skeleton className={"h-10 w-full"} />}
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
